import React, { useContext, useState } from "react";
import { AuthContext } from "../App";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, ExternalLink, Users, Send } from "lucide-react";

// Configure your WhatsApp group link here
const WHATSAPP_GROUP_LINK = process.env.REACT_APP_WHATSAPP_GROUP || "https://chat.whatsapp.com/EJfn5klFD81DuXWrHWdwap?mode=wwc";

function Chat() {
  const { user } = useContext(AuthContext);
  const [message, setMessage] = useState("");

  const openWhatsAppGroup = () => {
    window.open(WHATSAPP_GROUP_LINK, '_blank');
  };

  const sendWhatsAppMessage = () => {
    if (!message.trim()) return;
    
    // Format: whatsapp://send?text=YOUR_MESSAGE
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    setMessage("");
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="chat-container">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-100 mb-2 flex items-center">
            <MessageCircle className="w-8 h-8 mr-3 text-cyan-400" />
            Community Chat
          </h1>
          <p className="text-slate-400">Connect with fellow traders on WhatsApp</p>
        </div>

        {/* WhatsApp Group Card */}
        <Card className="bg-gradient-to-br from-emerald-900/30 to-green-900/30 border-emerald-800/50 p-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-100 mb-1">
                  Superior Traders WhatsApp Group
                </h2>
                <p className="text-emerald-200">
                  Join our exclusive trading community for real-time discussions, signals, and market insights
                </p>
              </div>
            </div>
            <Button
              onClick={openWhatsAppGroup}
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white border-0 whitespace-nowrap"
              data-testid="join-whatsapp-button"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Join Group
            </Button>
          </div>
        </Card>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-slate-800/50 border-slate-700 p-6">
            <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6 text-cyan-400" />
            </div>
            <h3 className="text-lg font-bold text-slate-100 mb-2">Real-Time Chat</h3>
            <p className="text-slate-400 text-sm">
              Discuss trades, share ideas, and get instant feedback from experienced traders
            </p>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 p-6">
            <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-cyan-400" />
            </div>
            <h3 className="text-lg font-bold text-slate-100 mb-2">Expert Community</h3>
            <p className="text-slate-400 text-sm">
              Connect with top-performing traders and learn from their strategies
            </p>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 p-6">
            <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
              <Send className="w-6 h-6 text-cyan-400" />
            </div>
            <h3 className="text-lg font-bold text-slate-100 mb-2">Trading Signals</h3>
            <p className="text-slate-400 text-sm">
              Receive exclusive trading signals and market analysis from our experts
            </p>
          </Card>
        </div>

        {/* Quick Message */}
        <Card className="bg-slate-800/50 border-slate-700 p-8">
          <h2 className="text-2xl font-bold text-slate-100 mb-4">Send Quick Message</h2>
          <p className="text-slate-400 mb-6">
            Compose a message to share with the trading community
          </p>
          
          <div className="flex space-x-4">
            <Input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 bg-slate-700 border-slate-600 text-slate-100"
              onKeyPress={(e) => e.key === 'Enter' && sendWhatsAppMessage()}
              data-testid="message-input"
            />
            <Button
              onClick={sendWhatsAppMessage}
              disabled={!message.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white border-0"
              data-testid="send-message-button"
            >
              <Send className="w-5 h-5 mr-2" />
              Send via WhatsApp
            </Button>
          </div>
        </Card>

        {/* Group Rules */}
        <Card className="bg-slate-800/50 border-slate-700 p-8">
          <h2 className="text-2xl font-bold text-slate-100 mb-4">Group Guidelines</h2>
          <div className="space-y-3 text-slate-300">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-cyan-400 text-sm font-bold">1</span>
              </div>
              <p>Respect all members and their trading strategies</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-cyan-400 text-sm font-bold">2</span>
              </div>
              <p>Share valuable insights and constructive feedback</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-cyan-400 text-sm font-bold">3</span>
              </div>
              <p>No spam, promotional content, or external links without approval</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-cyan-400 text-sm font-bold">4</span>
              </div>
              <p>Trading carries risks - always do your own research (DYOR)</p>
            </div>
          </div>
        </Card>

        {/* Admin setup instructions removed */}
      </div>
    </Layout>
  );
}

export default Chat;
